import React, { useState } from "react";
import { toast } from "sonner";
import { api, COMPANY_PHONE, COMPANY_EMAIL, COMPANY_ADDRESS, WHATSAPP_NUMBER } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, MessageCircle, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill name, phone and message");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/contact", form);
      setDone(true);
      toast.success("Thanks! We'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally { setSubmitting(false); }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 bg-gradient-to-r from-[#EBF2FF] to-white border border-[#E4E4E7] rounded-2xl px-6 py-4 flex items-center gap-3" data-testid="contact-statewide-banner">
          <div className="h-9 w-9 rounded-full bg-[#0052FF] text-white flex items-center justify-center">
            <MapPin size={16} />
          </div>
          <div>
            <div className="font-display font-bold text-[#06152D]">Services Available Across Telangana</div>
            <div className="text-xs text-[#52525B]">From Siddipet to every district — one advisor, one phone call away.</div>
          </div>
        </div>
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0052FF]">Contact</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#06152D]">Let's talk about your loan.</h1>
          <p className="mt-4 text-[#52525B] max-w-md">Reach us any of these ways — a Loan Junction advisor responds within one business day.</p>
          <div className="mt-8 space-y-4">
            <a href={`tel:${COMPANY_PHONE.replace(/\s/g, "")}`} className="flex items-center gap-3 group" data-testid="contact-phone">
              <div className="h-11 w-11 rounded-xl bg-[#EBF2FF] flex items-center justify-center text-[#0052FF]"><Phone size={18} /></div>
              <div>
                <div className="text-xs text-[#52525B]">Phone</div>
                <div className="font-semibold text-[#06152D] group-hover:text-[#0052FF]">{COMPANY_PHONE}</div>
              </div>
            </a>
            <a href={`mailto:${COMPANY_EMAIL}`} className="flex items-center gap-3 group" data-testid="contact-email">
              <div className="h-11 w-11 rounded-xl bg-[#EBF2FF] flex items-center justify-center text-[#0052FF]"><Mail size={18} /></div>
              <div>
                <div className="text-xs text-[#52525B]">Email</div>
                <div className="font-semibold text-[#06152D] group-hover:text-[#0052FF]">{COMPANY_EMAIL}</div>
              </div>
            </a>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group" data-testid="contact-whatsapp">
              <div className="h-11 w-11 rounded-xl bg-[#25D366]/10 flex items-center justify-center text-[#25D366]"><MessageCircle size={18} /></div>
              <div>
                <div className="text-xs text-[#52525B]">WhatsApp</div>
                <div className="font-semibold text-[#06152D] group-hover:text-[#25D366]">Chat now</div>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-[#EBF2FF] flex items-center justify-center text-[#0052FF]"><MapPin size={18} /></div>
              <div>
                <div className="text-xs text-[#52525B]">Office</div>
                <div className="font-semibold text-[#06152D]">{COMPANY_ADDRESS}</div>
              </div>
            </div>
          </div>
        </div>

        {done ? (
          <div className="bg-[#F4F4F5] border border-[#E4E4E7] rounded-2xl p-8 flex flex-col items-center justify-center text-center">
            <CheckCircle2 size={48} className="text-[#059669]" />
            <h2 className="mt-4 font-display text-2xl font-bold text-[#06152D]">Message received</h2>
            <p className="mt-2 text-[#52525B]">We'll get back to you within one business day.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#F4F4F5] border border-[#E4E4E7] rounded-2xl p-6 sm:p-8 space-y-4" data-testid="contact-form">
            <div><Label>Full name *</Label><Input value={form.name} onChange={set("name")} required data-testid="contact-name" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="contact-email-input" /></div>
              <div><Label>Phone *</Label><Input value={form.phone} onChange={set("phone")} required data-testid="contact-phone-input" /></div>
            </div>
            <div><Label>Subject</Label><Input value={form.subject} onChange={set("subject")} data-testid="contact-subject" /></div>
            <div><Label>Message *</Label><Textarea rows={5} value={form.message} onChange={set("message")} required data-testid="contact-message" /></div>
            <button type="submit" disabled={submitting} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60" data-testid="contact-submit">
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        )}
      </div>
      </div>
    </section>
  );
}
